import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n';
import { useNotifications } from '../hooks/useNotifications';
import styles from './NotificationBell.module.css';

interface NotificationBellProps {
  // 'header' = inline in a horizontal menu (logged-in Layout header).
  // 'floating' = absolutely positioned top-left (login page).
  variant?: 'header' | 'floating';
}

// Render *bold* spans inside an otherwise plain body string. Asterisks are an
// editor-friendly shorthand for the notifications file so highlighted numbers
// (e.g. "*114*") show up without needing real markdown rendering.
function renderBoldSpans(text: string) {
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <strong key={i}>{part.slice(1, -1)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

export function NotificationBell({ variant = 'header' }: NotificationBellProps) {
  const { language } = useLanguage();
  const { items, readIds, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const label = language === 'tr' ? 'Bildirimler' : 'Notifications';
  const emptyLabel = language === 'tr' ? 'Bildirim yok.' : 'No notifications.';
  const markAllLabel = language === 'tr' ? 'Tümünü okundu say' : 'Mark all as read';

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${variant === 'floating' ? styles.floating : ''}`}
    >
      <button
        type="button"
        className={styles.bellBtn}
        onClick={() => setOpen(o => !o)}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        title={label}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {unreadCount > 0 && (
          <span className={styles.badge} aria-label={`${unreadCount} unread`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label={label}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>{label}</span>
            {unreadCount > 0 && (
              <button type="button" className={styles.markAllBtn} onClick={markAllRead}>
                {markAllLabel}
              </button>
            )}
          </div>

          <div className={styles.list}>
            {items.length === 0 ? (
              <div className={styles.empty}>{emptyLabel}</div>
            ) : (
              items.map(n => {
                const isRead = readIds.has(n.id);
                return (
                  <article
                    key={n.id}
                    className={`${styles.item} ${isRead ? styles.itemRead : styles.itemUnread}`}
                  >
                    <div className={styles.itemHead}>
                      {!isRead && <span className={styles.unreadDot} aria-hidden="true" />}
                      <h4 className={styles.itemTitle}>{n.title[language]}</h4>
                      <time className={styles.itemDate} dateTime={n.date}>{n.date}</time>
                    </div>
                    <p className={styles.itemBody}>{renderBoldSpans(n.body[language])}</p>
                    {n.signature && (
                      <p className={styles.itemSignature}>- {n.signature[language]}</p>
                    )}
                    {!isRead && (
                      <button
                        type="button"
                        className={styles.markReadBtn}
                        onClick={() => markRead(n.id)}
                      >
                        {language === 'tr' ? 'Okundu olarak işaretle' : 'Mark as read'}
                      </button>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
